// Dedicated Dashboard Store - Phase 3: State Management Optimization
import { create } from 'zustand';
import { subscribeWithSelector, devtools } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';

interface DashboardData {
  recentRuns?: any[];
  recentFitness?: any[];
  bodyComposition?: any;
  photos?: any[];
  goals?: any[];
  achievements?: any[];
  lastUpdated?: number;
}

interface DashboardState {
  data: DashboardData;
  loading: boolean;
  error: string | null;
}

interface DashboardActions {
  setData: (data: Partial<DashboardData>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearData: () => void;
  refreshData: (userId: string) => Promise<void>;
  isStale: () => boolean;
}

type DashboardStore = DashboardState & DashboardActions;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useDashboardStore = create<DashboardStore>()(
  devtools(
    subscribeWithSelector(
      (set, get) => ({
        // Initial state
        data: {},
        loading: false,
        error: null,

        // Actions
        setData: (newData) => {
          set((state) => ({
            data: { 
              ...state.data, 
              ...newData, 
              lastUpdated: Date.now() 
            },
          }));
        },

        setLoading: (loading) => {
          set({ loading });
        },

        setError: (error) => {
          set({ error });
        },

        clearData: () => {
          set({ data: {}, error: null });
        },

        isStale: () => {
          const { data } = get();
          if (!data.lastUpdated) return true;
          return Date.now() - data.lastUpdated > CACHE_DURATION;
        },

        refreshData: async (userId: string) => {
          try {
            set({ loading: true, error: null });

            const { data, error } = await supabase.rpc('get_optimized_dashboard_summary', {
              p_user_id: userId,
              p_limit: 10
            });

            if (error) throw error;

            const dashboardData = {
              recentRuns: Array.isArray(data?.[0]?.recent_runs) ? data[0].recent_runs : [],
              recentFitness: Array.isArray(data?.[0]?.recent_fitness) ? data[0].recent_fitness : [],
              bodyComposition: data?.[0]?.latest_body_comp || null,
              photos: [],
              goals: [],
              achievements: Array.isArray(data?.[0]?.personal_records) ? data[0].personal_records : [],
              lastUpdated: Date.now()
            };

            set({ data: dashboardData, loading: false });

          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data';
            set({ error: errorMessage, loading: false });
            console.error('Dashboard refresh error:', error);
          }
        },
      })
    ),
    { name: 'Dashboard Store' }
  )
);

// Optimized selectors
export const useDashboardData = () => useDashboardStore((state) => state.data);
export const useDashboardLoading = () => useDashboardStore((state) => state.loading);
export const useDashboardError = () => useDashboardStore((state) => state.error);
export const useRecentRuns = () => useDashboardStore((state) => state.data.recentRuns || []);
export const useRecentFitness = () => useDashboardStore((state) => state.data.recentFitness || []);
export const useAchievements = () => useDashboardStore((state) => state.data.achievements || []);